/**
 * 详情页 URL 构造纯函数（F10，ADR-0001 修订 Q10）。
 *
 * **slug 语义**：新版展示 URL 用 `/detail/<title-slug>`，避免把后端 Guid
 * 暴露在前台地址栏。旧版 `/detail/<sectionId>/<slug?>` 深链仍由 DetailPage
 * 兼容解析，并在命中 Section 后用 `history.replaceState` 修正为短 URL。
 *
 * **中文 slug**：用 Unicode property escapes 保留汉字 / 字母 / 数字，
 * 非词字符（空格 / 标点 / 括号）作分隔符压成单 `-`。例：
 *   `成果简介`        → `成果简介`
 *   `1. 成果简介`     → `1-成果简介`
 *   `Hello World`     → `hello-world`
 *   `成果简介(SNAIL)` → `成果简介-snail`
 * 空标题 / 全非词字符 → 空串，路径退化为 `/detail/<id>`。
 *
 * **Preview 透传**（F10 基础透传，与 F15 闭合）：预览态构造 location 时
 * 携 `?preview=<token>` query，sidebar / 更多+ 跳转 URL 在 isPreview 时
 * 不丢 token。token 来源 `store.state.previewToken`（bootstrap 探测写入）。
 */
import type { PublicSectionView } from '@/api/types';

const SLUG_SEPARATOR = '-';
const SLUG_TOKEN_PATTERN = /[\p{L}\p{N}]+/gu;
const PREVIEW_QUERY_KEY = 'preview';

/** 标题转 kebab slug（保留汉字 / 字母 / 数字，非词字符压成单 `-`）。空标题返空串。 */
export function titleToSlug(title: string): string {
  const tokens = title.match(SLUG_TOKEN_PATTERN);
  if (tokens === null) {
    return '';
  }
  return tokens.join(SLUG_SEPARATOR).toLowerCase();
}

/** 对外展示用详情页 key：优先标题 slug，标题不可表达时才退回稳定 id。 */
export function detailKeyFor(section: PublicSectionView): string {
  return titleToSlug(section.title) || section.id;
}

/** 兼容短 slug 与旧版 id 深链：先精确匹配 id，再按标题 slug 匹配。 */
export function findSectionByDetailKey(
  sections: readonly PublicSectionView[],
  key: string,
): PublicSectionView | undefined {
  return sections.find((section) => section.id === key)
    ?? sections.find((section) => titleToSlug(section.title) === key);
}

/**
 * `router.push` 用的 location 对象。预览态携 `?preview=<token>` query，
 * anchor 非空则带 hash；标题 slug 空时 path 退化为 `/detail/<id>`。
 */
export function detailRouteLocation(
  section: PublicSectionView,
  options: { readonly anchor?: string | null; readonly previewToken?: string | null } = {},
): {
  readonly path: string;
  readonly query: Readonly<Record<string, string>>;
  readonly hash: string;
} {
  const { anchor = null, previewToken = null } = options;
  const path = `/detail/${detailKeyFor(section)}`;
  const hash = anchor ? `#${anchor}` : '';
  const query: Record<string, string> = {};
  if (previewToken) {
    query[PREVIEW_QUERY_KEY] = previewToken;
  }
  return { path, query, hash };
}

/** `/detail/<slug-or-id>[#anchor]` 字符串形态（不含 query，供 `<a href>` / replaceState 用）。 */
export function detailPathFor(
  section: PublicSectionView,
  anchor?: string | null,
): string {
  const location = detailRouteLocation(section, { anchor });
  return `${location.path}${location.hash}`;
}
